import type { Dash } from '@vp/shared-types';
import type { NodeShape } from './model';

/**
 * Laid-out diagram in its own coordinates (origin top-left, units = canvas px at 100%).
 * The renderer turns it into platform primitives; "explode" turns it into canvas objects.
 */
export type GeoShape = NodeShape | 'participant' | 'actor' | 'activation' | 'note';

export interface GeoNode {
  k: 'node';
  id: string;
  shape: GeoShape;
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  fill: string;
  stroke: string;
  text: string;
  width: number;
  strokeDash: Dash;
  fontSize: number;
  bold?: boolean;
  flip?: boolean;
}

export interface GeoEdge {
  k: 'edge';
  /** Ids of the connected nodes (flowcharts), for explode. */
  from?: string;
  to?: string;
  points: number[];
  arrowStart: boolean;
  arrowEnd: boolean;
  /** Open (line) arrowhead instead of a filled one. */
  openArrow?: boolean;
  dash: Dash;
  width: number;
  color: string;
  label?: { text: string; x: number; y: number; w: number; h: number; fontSize: number; color: string; plain?: boolean };
}

export interface GeoGroup {
  k: 'group';
  id: string;
  title: string;
  x: number;
  y: number;
  w: number;
  h: number;
  fill: string;
  stroke: string;
  titleH: number;
  fontSize: number;
}

/** Sequence block (loop / alt / opt / par) with optional section separators. */
export interface GeoFrame {
  k: 'frame';
  tag: string;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  sections: { y: number; label: string }[];
  stroke: string;
  fontSize: number;
}

export interface GeoText {
  k: 'text';
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  fontSize: number;
  align: 'left' | 'center' | 'right';
  bold?: boolean;
}

export type GeoItem = GeoNode | GeoEdge | GeoGroup | GeoFrame | GeoText;

export interface Geometry {
  kind: 'flow' | 'sequence';
  /** Flowcharts: how links are drawn. */
  links?: 'orthogonal' | 'curved';
  width: number;
  height: number;
  /** Back to front. */
  items: GeoItem[];
}

/** Text width in px for a font size (injected: a DOM canvas in the app, an estimate elsewhere). */
export type Measure = (text: string, fontSize: number, bold: boolean) => number;

/** Rough width estimate without a DOM (tests, workers, module runtime). */
export const approxMeasure: Measure = (text, fontSize, bold) => {
  let w = 0;
  for (const ch of text) {
    if (/[\p{Lu}]/u.test(ch)) w += 0.66;
    else if (/[ilj.,:;'!|]/.test(ch)) w += 0.3;
    else if (/\s/.test(ch)) w += 0.3;
    else if (/[mwMWшщжмюЖШЩМЮ]/.test(ch)) w += 0.8;
    else w += 0.56;
  }
  return w * fontSize * (bold ? 1.06 : 1);
};
